import { inject, Injectable } from '@angular/core';
import { parseISO } from 'date-fns';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  ClassificationCode,
  IAddPromotionCodeDto,
  IApplyPromotionCodeDto,
  IPromotionCodeDto,
  IReferralPromoCodeDto,
  IUnApplyPromotionCodeDto,
  IValidatePromotionCodeDto,
  TranslationKeys,
} from 'src/app/contracts';
import { Api } from 'src/app/core/common/api';
import { IApiOptions } from 'src/app/core/contracts/core.types';
import { UI } from 'src/app/core/helpers/ui';
import { toHttpParams } from 'src/app/core/helpers/utils';
import { mapClassificationToServiceType, PaymentService } from 'src/app/services/payment.service';

@Injectable({ providedIn: 'root' })
export class PromotionCodesData {
  private api = inject(Api);
  private ui = inject(UI);

  public codes = new BehaviorSubject<IPromotionCodeDto[]>([]);
  public get codes$() {
    return this.codes.asObservable();
  }
  public getPromotionCodes(classification: ClassificationCode, forceUpdate?: boolean, options?: IApiOptions): Promise<IPromotionCodeDto[]> {
    return new Promise(resolve => {
      if (forceUpdate || this.codes.getValue().length === 0) {
        this.api
          .get<IPromotionCodeDto[]>(`promotioncodes?serviceType=${mapClassificationToServiceType(classification)}`, options)
          .pipe(
            catchError(err => {
              resolve(this.codes.getValue());
              return throwError(() => err as unknown);
            })
          )
          .subscribe((codes: IPromotionCodeDto[]) => {
            for (const code of codes) {
              this.formatCodeProperties(code);
            }
            this.codes.next(codes);
            resolve(codes);
          });
      } else {
        resolve(this.codes.getValue());
      }
    });
  }
  public validate(promotionCodeId: number, requestId: number, classification: ClassificationCode) {
    return this.api.post('promotioncodes/validate', {
      promotionCodeId,
      requestId,
      serviceType: mapClassificationToServiceType(classification),
    } as IValidatePromotionCodeDto);
  }
  public addPromotionCode(code: string, classification: ClassificationCode): Observable<IPromotionCodeDto> {
    return this.api
      .post<IPromotionCodeDto>('promotioncodes', { promotionCode: code, serviceType: mapClassificationToServiceType(classification) } as IAddPromotionCodeDto)
      .pipe(
        tap(code => {
          this.formatCodeProperties(code);
        })
      );
  }
  private formatCodeProperties(code: IPromotionCodeDto) {
    if (code) {
      code.formattedValidFrom = this.ui.formatDate(parseISO(code.validFrom));
      code.formattedValidTo = this.ui.formatDate(parseISO(code.validTo));
    }
  }

  public unApplyPromotionCode(promotionCodeId: number, requestId: number, classification: ClassificationCode) {
    return this.api.post('promotioncodes/unapply', {
      promotionCodeId,
      requestId,
      serviceType: mapClassificationToServiceType(classification),
    } as IUnApplyPromotionCodeDto);
  }
  public getPromotionCode(promotionCodeId: number): Observable<IPromotionCodeDto> {
    return this.api.get<IPromotionCodeDto>(`promotioncodes/${promotionCodeId}`).pipe(
      tap(code => {
        if (code) {
          code.formattedValidFrom = this.ui.formatDate(parseISO(code.validFrom));
          code.formattedValidTo = this.ui.formatDate(parseISO(code.validTo));
        }
      })
    );
  }
  public applyPromotionCode(promotionCodeId: number, requestId: number, classification: ClassificationCode) {
    return this.api.post('promotioncodes/apply', {
      promotionCodeId,
      requestId,
      serviceType: mapClassificationToServiceType(classification),
    } as IApplyPromotionCodeDto);
  }
  public generateReferralPromoCode(platform: string): Observable<IReferralPromoCodeDto> {
    return this.api.get('promotioncodes/referral', { params: toHttpParams({ platform }) });
  }
  public getPromoCodeDiscount(code: IPromotionCodeDto, classification: ClassificationCode) {
    if (code) {
      if (PaymentService.isHourly(classification)) {
        const discountAmount = code.hourlyDiscount;
        const unit: string = code.isHourlyPercentDiscount ? '%' : this.ui.translate.instant(TranslationKeys.DomainCommon.SAR);
        return `${discountAmount} ${unit}`;
      }
      if (PaymentService.isMonthly(classification)) {
        const discountAmount = code.monthlyDiscount;
        const unit: string = code.isMonthlyPercentDiscount ? '%' : this.ui.translate.instant(TranslationKeys.DomainCommon.SAR);
        return `${discountAmount} ${unit}`;
      }
    }
    return '';
  }
  public dispose() {
    this.codes.next([]);
    return Promise.resolve();
  }
}
